import { ApiConfig, ApiResult } from "common/models";
import { HttpClient } from "common/services/http-client.service";
import { buildEngApiUrl } from "common/utils/api.util";
import {
  CreatePidAnalysisRecordRequest,
  KnowledgeGraphGenerationResponse,
  PidAnalysisRecord,
  PidAnalysisResponse,
  PidAnalysisReviewSummary
} from "../models/pid-analysis.model";
import { KnowledgeGraphResponse } from "../models/knowledge-graph.model";

export class PidAnalysisService extends HttpClient {
  public static async getPidAnalysis(
    documentGuidCode: string
  ): Promise<ApiResult<PidAnalysisResponse>> {
    const basePath = `mocs/doc/${documentGuidCode}/pid-analysis`;

    const firstResponse = await this.invokeApi<PidAnalysisResponse>(
      new ApiConfig(buildEngApiUrl(basePath), "GET")
    );

    const first = firstResponse.value;
    if (firstResponse.errors?.length || !first) {
      return new ApiResult<PidAnalysisResponse>(
        first,
        firstResponse.statusCode,
        ...firstResponse.errors
      );
    }

    const records: PidAnalysisRecord[] = [...(first.records ?? [])];
    const totalPages = first.totalPages ?? 1;
    const totalRecords = first.totalRecords ?? records.length;
    const firstPage = first.page ?? 1;

    // Page through remaining pages using the size the API applied, so no page size is hardcoded.
    for (
      let page = firstPage + 1;
      page < firstPage + totalPages && records.length < totalRecords;
      page++
    ) {
      const pageResponse = await this.invokeApi<PidAnalysisResponse>(
        new ApiConfig(buildEngApiUrl(basePath), "GET", `page=${page}`)
      );
      const pageRecords = pageResponse.value?.records;
      if (!pageRecords?.length) break;
      records.push(...pageRecords);
    }

    return new ApiResult<PidAnalysisResponse>(
      { ...first, records, totalRecords: records.length },
      firstResponse.statusCode,
      ...firstResponse.errors
    );
  }

  public static async addPidAnalysisRecord(
    documentGuidCode: string,
    payload: CreatePidAnalysisRecordRequest
  ): Promise<ApiResult<PidAnalysisRecord>> {
    const config = new ApiConfig(
      buildEngApiUrl(`mocs/doc/${documentGuidCode}/pid-analysis/record`),
      "POST",
      "",
      payload
    );
    const response = await this.invokeApi<PidAnalysisRecord>(config);
    return new ApiResult<PidAnalysisRecord>(
      response.value,
      response.statusCode,
      ...response.errors
    );
  }

  public static async updatePidAnalysisRecord(
    analysisId: string,
    payload: CreatePidAnalysisRecordRequest
  ): Promise<ApiResult<PidAnalysisRecord>> {
    const config = new ApiConfig(
      buildEngApiUrl(`mocs/pid-analysis/${analysisId}`),
      "PUT",
      "",
      payload
    );
    const response = await this.invokeApi<PidAnalysisRecord>(config);
    return new ApiResult<PidAnalysisRecord>(
      response.value,
      response.statusCode,
      ...response.errors
    );
  }

  public static async deletePidAnalysisRecords(
    analysisIds: string[]
  ): Promise<ApiResult<void>> {
    const config = new ApiConfig(
      buildEngApiUrl(`mocs/pid-analysis`),
      "DELETE",
      "",
      { analysisIds }
    );
    const response = await this.invokeApi<void>(config);
    return new ApiResult<void>(
      response.value,
      response.statusCode,
      ...response.errors
    );
  }

  public static async acceptPidAnalysisRecords(
    analysisIds: string[]
  ): Promise<ApiResult<void>> {
    const config = new ApiConfig(
      buildEngApiUrl(`mocs/pid-analysis/accept-selected`),
      "PATCH",
      "",
      { analysisIds }
    );
    const response = await this.invokeApi<void>(config);
    return new ApiResult<void>(
      response.value,
      response.statusCode,
      ...response.errors
    );
  }

  public static async submitReviewSummary(
    documentGuidCode: string
  ): Promise<ApiResult<PidAnalysisReviewSummary>> {
    const config = new ApiConfig(
      buildEngApiUrl(
        `mocs/doc/${documentGuidCode}/pid-analysis/review-summary`
      ),
      "POST"
    );
    const response = await this.invokeApi<PidAnalysisReviewSummary>(config);
    return new ApiResult<PidAnalysisReviewSummary>(
      response.value,
      response.statusCode,
      ...response.errors
    );
  }

  public static async generateKnowledgeGraph(
    documentGuidCode: string
  ): Promise<ApiResult<KnowledgeGraphGenerationResponse>> {
    const config = new ApiConfig(
      buildEngApiUrl(`mocs/doc/${documentGuidCode}/knowledge-graph`),
      "POST"
    );
    const response = await this.invokeApi<KnowledgeGraphGenerationResponse>(
      config
    );
    return new ApiResult<KnowledgeGraphGenerationResponse>(
      response.value,
      response.statusCode,
      ...response.errors
    );
  }

  public static async getKnowledgeGraph(
    documentGuidCode: string
  ): Promise<ApiResult<KnowledgeGraphResponse>> {
    const config = new ApiConfig(
      buildEngApiUrl(`mocs/doc/${documentGuidCode}/knowledge-graph`),
      "GET"
    );
    const response = await this.invokeApi<KnowledgeGraphResponse>(config);
    return new ApiResult<KnowledgeGraphResponse>(
      response.value,
      response.statusCode,
      ...response.errors
    );
  }

  // Returns a preview of the knowledge graph built from the current relationship
  // edits, before it is generated.
  public static async previewKnowledgeGraph(
    documentGuidCode: string
  ): Promise<ApiResult<KnowledgeGraphResponse>> {
    const config = new ApiConfig(
      buildEngApiUrl(`mocs/preview/${documentGuidCode}`),
      "GET"
    );
    const response = await this.invokeApi<KnowledgeGraphResponse>(config);
    return new ApiResult<KnowledgeGraphResponse>(
      response.value,
      response.statusCode,
      ...response.errors
    );
  }
}
